// §7.1 — the VFS tree: the layout, the `/System` rule, and `watch()` fan-out.
//
// `opfs.ts` is bytes; this is the filesystem the shell and `HangarAPI.fs` actually talk to. It
// owns three things the byte layer has no opinion about:
//
//  1. **The §7.1 layout**, bootstrapped once per user on first touch of a fresh origin.
//  2. **`/System` is read-only.** Not per-caller — that is `confine()`'s job (§9.5) — but as an
//     invariant of the tree itself. The one exception is the kernel's own store under
//     `/System/.versions` (§7.5), which is reachable through `internal` and nowhere else.
//  3. **Change notification.** Every mutation produces one `FsEvent`, delivered to the watchers
//     whose directory contains it and published on the bus as `fs:changed`.
import {
  ROOT_APPS,
  ROOT_SYSTEM,
  ROOT_USERS,
  USER_FOLDERS,
  basename,
  isUnder,
  joinVfs,
  normalizeVfsPath,
  parentOf,
  trashRoot,
  userRoot,
  type FileEntry,
  type FsEvent,
  type VfsPath,
} from '@hangar/shared';
import type { Bus, Unsubscribe } from '../bus';
import { OpfsError, type OpfsStat, type OpfsStore } from './opfs';

/** The kernel's own area under `/System` (§7.5). Everything else there is immutable. */
export const SYSTEM_VERSIONS = `${ROOT_SYSTEM}/.versions`;

export type VfsErrorCode = 'READ_ONLY' | 'REFUSED';

export class VfsError extends Error {
  readonly code: VfsErrorCode;
  readonly path: string;

  constructor(code: VfsErrorCode, path: string, message?: string) {
    super(message ?? `vfs: ${code} for ${JSON.stringify(path)}`);
    this.name = 'VfsError';
    this.code = code;
    this.path = path;
  }
}

/** The unrestricted view. Only kernel services hold one — versions (§7.5), trash (§7.8). */
export interface VfsInternal {
  read(path: string): Promise<Uint8Array>;
  write(path: string, data: Uint8Array | string): Promise<FileEntry>;
  list(dir: string): Promise<FileEntry[]>;
  mkdir(path: string): Promise<FileEntry>;
  move(from: string, to: string): Promise<FileEntry>;
  remove(path: string, options?: { recursive?: boolean }): Promise<void>;
  stat(path: string): Promise<FileEntry | null>;
  exists(path: string): Promise<boolean>;
}

export interface VfsTree extends VfsInternal {
  /**
   * Create the §7.1 layout for the current user. Idempotent, and implied by every other call.
   *
   * `force` re-seeds a space that has been deleted out from under it (§11.6's reset). Without it
   * the once-per-session cache would report success and leave the user with no folders.
   */
  bootstrap(force?: boolean): Promise<void>;
  readText(path: string): Promise<string>;
  /** Events at or under `dir`. Returns the unsubscribe. */
  watch(dir: string, handler: (event: FsEvent) => void): Unsubscribe;
  /** The user whose space this tree is rooted at — `guest` when anonymous (§7.1). */
  currentUser(): string;
  /** The `/System`-writable view. Not reachable from `HangarAPI`. */
  readonly internal: VfsInternal;
  /**
   * §18 F16 — a change another tab of this origin made. The cached handles it invalidates are
   * dropped and the event is delivered to local watchers, so an Explorer here sees a file
   * created there without a reload. Never re-published: that would ping-pong.
   */
  applyRemote(event: FsEvent): void;
}

export interface VfsTreeOptions {
  store: OpfsStore;
  /** `guest` for an anonymous session (§7.1, §8.3). Re-read on every call, so sign-in works. */
  user: () => string;
  bus?: Bus;
  /**
   * §7.5's "every write appends a version", wired at composition rather than by importing the
   * version store — services do not import one another (§4.2). Never called for `/System`,
   * which is where the version store itself writes.
   *
   * A failure here is logged, not thrown: the user's bytes are already on disk, and telling
   * them the save failed would be a lie.
   */
  onWrite?: (path: VfsPath, bytes: Uint8Array) => Promise<void>;
  /** History follows a move, or a later file at the old path would inherit it. */
  onMove?: (from: VfsPath, to: VfsPath) => Promise<void>;
  /**
   * §7.4 — a write made **by this device**, so sync can mark the path dirty and push it.
   *
   * Deliberately *not* `onWrite`, which fires from `writeAt` and therefore from both views. Sync
   * applies the server's changes through `internal`; marking those dirty would push every pulled
   * file straight back, the far device would pull the new rev, mark it dirty, and push again —
   * two desktops trading revisions forever over a file nobody touched. Only the public view
   * fires this, which is exactly the set of writes a person made.
   */
  onLocalWrite?: (path: VfsPath, bytes: Uint8Array) => Promise<void> | void;
  /**
   * §7.8 — a removal made by this device. Same public-view-only rule as `onLocalWrite`, and for
   * the same reason: sync's own `applyRemote` removes files through `internal`, and treating that
   * as a local delete would push the server's own deletion back to it.
   */
  onLocalRemove?: (path: VfsPath) => Promise<void> | void;
  /**
   * §7.8 / §7.1 — a move made by this device. The *kernel* decides what a move means to sync,
   * because that depends on the Trash, and the Trash is a §7.8 concept the tree does not model:
   * a move into it is a delete, a move out of it is a restore, and a move between two ordinary
   * paths is a rename — which is a delete and a create, since §7.4's protocol has no move.
   */
  onLocalMove?: (from: VfsPath, to: VfsPath) => Promise<void> | void;
  /**
   * D-194 — a **folder** created by this device. Same public-view-only rule as `onLocalWrite`.
   *
   * It needs its own hook because a folder has no bytes: `onLocalWrite` hands over the content
   * to hash, and there is none to hand. Without this, a folder was never marked dirty and so was
   * never pushed — which is why an empty one never reached another device at all.
   */
  onLocalMkdir?: (path: VfsPath) => Promise<void> | void;
  /** §18 F16 — every *local* change, for publishing to the other tabs of this origin. */
  onLocalEvent?: (event: FsEvent) => void;
}

/** The three roots plus the current user's own root: structural, never removable. */
function isStructural(path: VfsPath, user: string): boolean {
  return (
    path === '/' ||
    path === ROOT_USERS ||
    path === ROOT_APPS ||
    path === ROOT_SYSTEM ||
    path === userRoot(user)
  );
}

function directoryEntry(path: VfsPath): FileEntry {
  return { path, name: basename(path), isDir: true, size: 0, mtime: 0, rev: 0 };
}

function toEntry(stat: OpfsStat): FileEntry {
  // `rev: 0` is §7.4's "never synced". The server assigns a real one on first upload; `sha` is
  // left absent until something has actually hashed the bytes (D-047).
  return {
    path: stat.path,
    name: stat.name,
    isDir: stat.isDir,
    size: stat.size,
    mtime: stat.mtime,
    rev: 0,
  };
}

export function createVfsTree(options: VfsTreeOptions): VfsTree {
  const { store, bus } = options;
  const watchers = new Map<number, { dir: VfsPath; handler: (event: FsEvent) => void }>();
  const bootstrapped = new Map<string, Promise<void>>();
  let watcherId = 0;

  function requirePath(input: string): VfsPath {
    const parsed = normalizeVfsPath(input);
    if (!parsed.ok) throw new OpfsError(parsed.error, input);
    return parsed.path;
  }

  function emit(event: FsEvent): void {
    // `/System` is read-only to every caller, so the only writes there are the kernel's own
    // bookkeeping — the version store's index and blobs (§7.5). Publishing those would make an
    // Explorer window watching `/` re-list on every save, for a change no one can see.
    if (isUnder(event.path, ROOT_SYSTEM)) return;

    deliver(event);
    options.onLocalEvent?.(event);
  }

  /** Hand an event to the bus and the watchers, whoever made the change. */
  function deliver(event: FsEvent): void {
    bus?.emit('fs:changed', event);
    // Copy first: a handler may unsubscribe — or subscribe — while being called.
    for (const { dir, handler } of [...watchers.values()]) {
      const touched = isUnder(event.path, dir) || (event.from != null && isUnder(event.from, dir));
      if (touched) handler(event);
    }
  }

  async function ready(force = false): Promise<void> {
    const user = options.user();
    // §11.6's "Reset guest session" wipes the layout and needs it back. The cache exists so the
    // seed runs once per user per session, which is exactly what stops it coming back by itself.
    if (force) bootstrapped.delete(user);
    let pending = bootstrapped.get(user);
    if (!pending) {
      pending = seed(user);
      bootstrapped.set(user, pending);
    }
    await pending;
  }

  /**
   * The §7.1 layout. Deliberately silent: it is the shape of the filesystem, not a change to
   * it, and no watcher can exist yet on a fresh origin anyway.
   */
  async function seed(user: string): Promise<void> {
    await store.mkdir(ROOT_APPS);
    await store.mkdir(ROOT_SYSTEM);
    const home = userRoot(user);
    for (const folder of USER_FOLDERS) await store.mkdir(joinVfs(home, folder));
    await store.mkdir(trashRoot(user));
  }

  // ── the `/System` rule ───────────────────────────────────────────────────────────────────

  function guardWrite(path: VfsPath): void {
    if (isUnder(path, ROOT_SYSTEM)) throw new VfsError('READ_ONLY', path);
  }

  function guardRemove(path: VfsPath): void {
    guardWrite(path);
    if (isStructural(path, options.user())) {
      throw new VfsError('REFUSED', path, `vfs: ${path} is part of the layout and cannot be removed`);
    }
  }

  // ── the shared implementation, used by both views ────────────────────────────────────────

  async function readAt(path: VfsPath): Promise<Uint8Array> {
    await ready();
    return store.read(path);
  }

  async function statAt(path: VfsPath): Promise<FileEntry | null> {
    await ready();
    const found = await store.stat(path);
    return found ? toEntry(found) : null;
  }

  async function listAt(dir: VfsPath): Promise<FileEntry[]> {
    await ready();
    return (await store.list(dir)).map(toEntry);
  }

  /**
   * The ancestors of `path` that do not exist yet, shallowest first. A write into
   * `Documents/deep/nested/` creates two directories on the way, and a File Explorer watching
   * `Documents` has to hear about them or its listing silently goes stale. The walk stops at the
   * first ancestor that exists, so the common case — the parent is right there — costs one stat.
   */
  async function missingAncestors(path: VfsPath): Promise<VfsPath[]> {
    const missing: VfsPath[] = [];
    let cursor = parentOf(path);
    while (cursor !== '/' && (await store.stat(cursor)) === null) {
      missing.unshift(cursor);
      cursor = parentOf(cursor);
    }
    return missing;
  }

  function announceDirectories(paths: readonly VfsPath[]): void {
    for (const path of paths) {
      emit({ kind: 'create', path, entry: directoryEntry(path) });
    }
  }

  /**
   * The side effects that hang off a mutation but must never fail it. The bytes are committed
   * by the time these run; a broken version store is a degraded feature, not a lost save.
   */
  async function afterMutation(work: () => Promise<void>, what: string): Promise<void> {
    try {
      await work();
    } catch (error) {
      console.warn(`vfs: ${what} failed`, error);
    }
  }

  async function writeAt(
    path: VfsPath,
    data: Uint8Array | string,
    /** True for the public view — a write this device made. False for `internal`, which is sync's. */
    local: boolean,
  ): Promise<FileEntry> {
    await ready();
    const existed = (await store.stat(path)) !== null;
    const created = existed ? [] : await missingAncestors(path);
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    const entry = toEntry(await store.write(path, bytes));
    announceDirectories(created);
    emit({ kind: existed ? 'modify' : 'create', path, entry });
    if (options.onWrite && !versionExempt(path)) {
      await afterMutation(() => onWriteHook(path, bytes), 'version record');
    }
    // §7.4 — `/System` is excluded because sync's own bookkeeping lives there; a dirty
    // `.sync.json` would try to push the record of what has been pushed.
    if (local && options.onLocalWrite && !isUnder(path, ROOT_SYSTEM)) {
      await afterMutation(async () => options.onLocalWrite?.(path, bytes), 'sync mark');
    }
    return entry;
  }

  /**
   * `/System` is where the version store writes, so versioning it would recurse forever.
   * `.Trash` holds a deleted file's *history*, carried in by the move — its sidecars are
   * bookkeeping and have no history of their own (§7.8).
   */
  function versionExempt(path: VfsPath): boolean {
    return isUnder(path, ROOT_SYSTEM) || isUnder(path, trashRoot(options.user()));
  }

  async function onWriteHook(path: VfsPath, bytes: Uint8Array): Promise<void> {
    await options.onWrite?.(path, bytes);
  }

  async function mkdirAt(path: VfsPath, local: boolean): Promise<FileEntry> {
    await ready();
    const existed = (await store.stat(path)) !== null;
    const created = existed ? [] : await missingAncestors(path);
    const entry = toEntry(await store.mkdir(path));
    announceDirectories(created);
    if (!existed) emit({ kind: 'create', path, entry });
    // D-194 — the ancestors as well as the target. `mkdir /a/b/c` on a device that had neither
    // `a` nor `b` creates three folders, and pushing only the last would leave the other two to
    // be invented as side effects on whichever device happened to write into them first.
    if (local && options.onLocalMkdir && !isUnder(path, ROOT_SYSTEM)) {
      for (const made of existed ? [] : [...created, path]) {
        await afterMutation(async () => options.onLocalMkdir?.(made), 'sync mkdir');
      }
    }
    return entry;
  }

  async function moveAt(from: VfsPath, to: VfsPath, local: boolean): Promise<FileEntry> {
    await ready();
    const created = await missingAncestors(to);
    const entry = toEntry(await store.move(from, to));
    announceDirectories(created);
    emit({ kind: 'move', path: to, from, entry });
    if (options.onMove && !isUnder(from, ROOT_SYSTEM) && !isUnder(to, ROOT_SYSTEM)) {
      await afterMutation(() => onMoveHook(from, to), 'version rename');
    }
    if (local && options.onLocalMove && !isUnder(from, ROOT_SYSTEM) && !isUnder(to, ROOT_SYSTEM)) {
      await afterMutation(async () => options.onLocalMove?.(from, to), 'sync move');
    }
    return entry;
  }

  async function onMoveHook(from: VfsPath, to: VfsPath): Promise<void> {
    await options.onMove?.(from, to);
  }

  async function removeAt(path: VfsPath, recursive: boolean, local: boolean): Promise<void> {
    await ready();
    await store.remove(path, { recursive });
    emit({ kind: 'delete', path });
    if (local && options.onLocalRemove && !isUnder(path, ROOT_SYSTEM)) {
      await afterMutation(async () => options.onLocalRemove?.(path), 'sync remove');
    }
  }

  // Every method is `async`, so a bad path *rejects* rather than throwing at the call site.
  // The one exception is `watch`, which is synchronous by signature (§4.1's `fs.watch` wraps it).
  const internal: VfsInternal = {
    async read(path) {
      return readAt(requirePath(path));
    },
    async write(path, data) {
      // `local: false` — this is the view sync writes the server's bytes through.
      return writeAt(requirePath(path), data, false);
    },
    async list(dir) {
      return listAt(requirePath(dir));
    },
    async mkdir(path) {
      // `local: false` — this is the view sync creates the server's folders through.
      return mkdirAt(requirePath(path), false);
    },
    async move(from, to) {
      return moveAt(requirePath(from), requirePath(to), false);
    },
    async remove(path, removeOptions) {
      return removeAt(requirePath(path), removeOptions?.recursive ?? false, false);
    },
    async stat(path) {
      return statAt(requirePath(path));
    },
    async exists(path) {
      return (await statAt(requirePath(path))) !== null;
    },
  };

  return {
    bootstrap: ready,
    currentUser: () => options.user(),
    internal,

    applyRemote(event) {
      if (isUnder(event.path, ROOT_SYSTEM)) return;
      // Whatever the other tab did, our cached handles for that path may now be lying.
      store.invalidate(event.path);
      if (event.from != null) store.invalidate(event.from);
      deliver(event);
    },

    read: internal.read,
    list: internal.list,
    stat: internal.stat,
    exists: internal.exists,

    async readText(path) {
      return new TextDecoder().decode(await internal.read(path));
    },

    async write(path, data) {
      const target = requirePath(path);
      guardWrite(target);
      return writeAt(target, data, true);
    },

    async mkdir(path) {
      const target = requirePath(path);
      guardWrite(target);
      return mkdirAt(target, true);
    },

    async move(from, to) {
      const source = requirePath(from);
      const destination = requirePath(to);
      guardRemove(source);
      guardWrite(destination);
      return moveAt(source, destination, true);
    },

    async remove(path, removeOptions) {
      const target = requirePath(path);
      guardRemove(target);
      return removeAt(target, removeOptions?.recursive ?? false, true);
    },

    watch(dir, handler) {
      const root = requirePath(dir);
      const id = (watcherId += 1);
      watchers.set(id, { dir: root, handler });
      let live = true;
      return () => {
        if (!live) return;
        live = false;
        watchers.delete(id);
      };
    },
  };
}
